import { filter, of, share, take, tap } from 'rxjs';
import { replaceTokens } from '@components/Utility/Template.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { map, switchMap } from 'rxjs/operators';
/*export function absoluteURL(url: string) {
    if (/^https?:\/\//i.test(url)) {
        return url;
    } else {
        // Get the current domain from window.location
        const currentDomain = window.location.origin;
        // Create an absolute URL by combining the domain and the provided relative URL
        return currentDomain + (url.startsWith('/') ? url : `/${url}`);
    }
}*/
export function absoluteURL(input) {
    const raw = (input || "").trim();
    if (!raw)
        return window.location.href;
    const origin = window.location.origin;
    const { protocol, host, hostname } = window.location;
    // 1) Already absolute with a known scheme? leave it alone
    if (/^(https?|ftp|file|ws|wss|mailto|tel|data|javascript):/i.test(raw)) {
        return raw;
    }
    // 2) Protocol-relative (e.g., //cdn.example.com/app.js)
    if (raw.startsWith("//")) {
        return protocol + raw;
    }
    // 3) Internal shortcuts
    if (raw.startsWith("/") || raw.startsWith("?") || raw.startsWith("#")) {
        return new URL(raw, origin).href;
    }
    // 4) Domain-like without a scheme? (e.g., example.com/foo or www.example.com)
    //    - If it's YOUR domain (same host or same root domain), treat as internal
    //    - Otherwise, prepend https://
    const domainLike = /^[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?::\d+)?(\/.*)?$/; // basic heuristic
    if (domainLike.test(raw)) {
        const candidate = new URL("https://" + raw); // parse safely with https://
        const candidateHost = candidate.hostname;
        // Simple root-domain extraction (best-effort; not PSL-perfect)
        const currentParts = hostname.split(".");
        const candidateParts = candidateHost.split(".");
        const currentRoot = currentParts.length >= 2 ? currentParts.slice(-2).join(".") : hostname;
        const candidateRoot = candidateParts.length >= 2 ? candidateParts.slice(-2).join(".") : candidateHost;
        const isSameHost = candidate.host === host;
        const isSameSite = candidateHost === hostname ||
            candidateHost.endsWith("." + hostname) || // subdomain of current host
            currentRoot === candidateRoot; // same root (e.g., app.x.com vs x.com)
        if (isSameHost || isSameSite) {
            // Treat as internal path on *this* origin
            const path = candidate.pathname + candidate.search + candidate.hash;
            return origin + path;
        }
        // External domain: force https
        return candidate.href;
    }
    // 5) Otherwise, treat as a relative path like "form/customer"
    return new URL(raw, origin).href;
}
export function isExternalUrl(url) {
    const parsedUrl = new URL(url);
    // Compare the hostname of the parsed URL with the current window's hostname
    return parsedUrl.hostname !== window.location.hostname;
}
/**
 * Extracts the query string params (search) from the given URL.
 *
 * @param {string} url - The URL from which to extract the params.
 * @returns {[key: string]: string} The params of the provided URL.
 * @example
 * const { pathname: route, params } = paramsFromUrlGet("https://q360rest.com/forms/customer?customerno=PRE001");
 * console.log(params);  // Outputs: {customerno: "PRE001"}
 * console.log(route); // Outputs: '/forms/customer
 */
export function paramsFromUrlGet(url) {
    if (!url) {
        return { pathname: '', params: {}, hash: '', formname: '' };
    }
    try {
        const fullUrl = absoluteURL(url);
        const urlObj = new URL(fullUrl);
        const searchParams = urlObj.searchParams;
        const pathname = urlObj.pathname;
        const hash = urlObj.hash;
        const params = {};
        for (const [key, value] of searchParams) {
            params[key] = value;
        }
        let formname = extractLastPathSegment(pathname);
        if (!formname) {
            formname = params._form || '';
        }
        return { pathname, params, hash, formname };
    }
    catch (e) {
        return { pathname: '', params: {}, hash: '', formname: '' };
    }
}
export function extractLastPathSegment(path) {
    if (!path || path === '/') {
        return '';
    }
    // Split the path by '/' and filter out empty strings
    const segments = path.split('/').filter(segment => segment.length > 0);
    // Return the last segment of the path
    return segments.pop() || ''; // Ensures an empty string is returned if no segments are found
}
/**
 * Parses a ZoomForm XML string and constructs a URL query string.
 *
 * @param {string} xmlString - The XML string representing a ZoomForm.
 * @return {string} - The constructed URL query string.
 * @throws {Error} - If 'webparamnames' and 'webparamvaluefields' do not have the same number of elements.
 */
export function parseResqZoomForm(xmlString) {
    if (!xmlString) {
        return '';
    }
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    const zoomFormXML = xmlDoc.getElementsByTagName('ZoomForm')[0];
    const getAttributeCaseInsensitive = (element, attributeName) => {
        for (const attr of element.attributes) {
            if (attr.name.toLowerCase() === attributeName.toLowerCase()) {
                return attr.value;
            }
        }
        return '';
    };
    const webaction = getAttributeCaseInsensitive(zoomFormXML, 'webaction').toLowerCase();
    const webparamnames = getAttributeCaseInsensitive(zoomFormXML, 'webparamnames').toLowerCase().split(',');
    const webhash = getAttributeCaseInsensitive(zoomFormXML, 'webhash') || '';
    const webparamvaluefields = splitByCommasOutsideBrackets(getAttributeCaseInsensitive(zoomFormXML, 'webparamvaluefields') || ''); //?.split(',');
    if (!webparamnames || !webparamvaluefields) {
        return `/form/${webaction}`;
    }
    // Ensure both arrays have the same length
    if (webparamnames.length !== webparamvaluefields.length) {
        throw new Error('webparamnames and webparamvaluefields must have the same number of elements.');
    }
    const queryString = webparamnames.map((param, index) => {
        let value = (webparamvaluefields[index] || '').trim();
        // to handle legacy expectations, [something] in the XML string means "something" is the literal parameter value.
        // whereas "something" in the xml string means something is a record column that contains the parameter value
        if (!value.startsWith('[') && !value.endsWith(']')) {
            value = `{{${value.toLowerCase()}}}`;
        }
        else if (value.startsWith('[') && value.endsWith(']')) {
            value = encodeURIComponent(value.slice(1, -1)); // Remove the square brackets
        }
        return `${param}=${value}`;
    }).join('&');
    return `/form/${webaction}?${queryString}${webhash ? '#' + webhash : ''}`;
}
function splitByCommasOutsideBrackets(input, stripBrackets = false) {
    const out = [];
    let buf = '';
    let depth = 0;
    for (let i = 0; i < input.length; i++) {
        const ch = input[i];
        if (ch === '[') {
            depth++;
            buf += ch;
        }
        else if (ch === ']') {
            depth = Math.max(0, depth - 1);
            buf += ch;
        }
        else if (ch === ',' && depth === 0) {
            const token = buf.trim();
            if (token) {
                out.push(stripBrackets && token.startsWith('[') && token.endsWith(']')
                    ? token.slice(1, -1)
                    : token);
            }
            buf = '';
        }
        else {
            buf += ch;
        }
    }
    const finalToken = buf.trim();
    if (finalToken) {
        out.push(stripBrackets && finalToken.startsWith('[') && finalToken.endsWith(']')
            ? finalToken.slice(1, -1)
            : finalToken);
    }
    return out;
}
/**
 * Handles the zoom functionality for opening URLs.
 *
 * @param {string} urlTemplate - The URL template to be used for generating the actual URL.
 * @param {object} options - Optional parameters for customizing the behavior of the zoom.
 *    @param {string} options.target - The target window or frame in which to open the URL. Defaults to an empty string.
 *    @param {object} options.data - The token-value pairs used for replacing tokens in the URL template.
 *    @param {Component} options.initiator - The component that initiated the zoom action.
 * @return {Observable} - An observable that emits a boolean value indicating success or failure of the zoom action.
 */
export function zoomHandler(urlTemplate, options) {
    urlTemplate = convertLegacyQ360Url(urlTemplate);
    let url = replaceTokens(urlTemplate, options?.data, { urlEncode: true });
    url = absoluteURL(url);
    const externalUrl = isExternalUrl(url);
    let urlTarget = options?.target || '';
    const ctrlKey = options?.event?.ctrlKey;
    const shiftKey = options?.event?.shiftKey;
    if (ctrlKey && shiftKey) {
        urlTarget = '_self';
    }
    else if (ctrlKey || shiftKey) {
        urlTarget = '_blank';
    }
    const openNewBrowserTab = externalUrl || urlTarget === '_blank';
    if (externalUrl) {
        const obs = DIContainer.getInstance('CodeTypeManager').getCodes('URLWHTLIST').pipe(map(codes => {
            const urlDomain = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
            return codes.some(code => {
                const codeValue = code.codevalue.toLowerCase();
                return urlDomain === codeValue || urlDomain.endsWith('.' + codeValue);
            });
        }), switchMap(isWhitelisted => {
            if (isWhitelisted) {
                return of(true);
            }
            else {
                return DIContainer.getInstance('Application').confirm({
                    caption: 'You are about to leave Q360 to an external website. \r\n\r\n' + url,
                    modalStyle: 'warning'
                });
            }
        }), tap(confirmed => {
            if (confirmed) {
                if (shiftKey && !ctrlKey) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
                else {
                    window.open(url);
                }
            }
        }), share());
        obs.subscribe();
        return obs;
    }
    else if (openNewBrowserTab) {
        const obs = of(true);
        obs.pipe(tap(() => {
            if (shiftKey) {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
            else {
                window.open(url);
            }
        })).subscribe();
        return obs;
    }
    else {
        let form;
        if (options?.initiator) {
            form = DIContainer.getInstance('ComponentRegistry').getFormComponentByElement(options.initiator.rootElement);
        }
        if (urlTarget === '_self' && !form && options?.recordNavigationDetail && options?.recordNavigationDetail.formId) {
            form = DIContainer.getInstance('ComponentRegistry').getComponent(options.recordNavigationDetail.formId);
        }
        let newWindow = urlTarget === '_top' || !form;
        if (!urlTarget) {
            // dyanmic mode, decide based on action.
            if (!form || url.indexOf(`/form/${form.propertyGet('formName')}`) > -1) {
                newWindow = true;
            }
            else {
                newWindow = true;
            }
        }
        let obs;
        if (newWindow) {
            const screen = DIContainer.getInstance('ScreenManager').open(url, {
                title: '',
                delayUntilRefreshed: !!options?.state
            });
            // event completes when the screen is told its form object, which should be after the form definition has loaded. 
            obs = screen.propertyGet$('currentForm')
                .pipe(filter((form) => !!form));
        }
        else if (form) {
            obs = form.navigate(url);
        }
        const setFormState = (form, state) => {
            form.records.main.state = state;
            form.updateTitle();
        };
        if (obs) {
            if (options?.recordNavigationDetail) {
                obs.pipe(tap((form) => {
                    options.recordNavigationDetail.formId = form.globalId;
                    form?.recordNavigationDetailSet(options?.recordNavigationDetail);
                }), share()).subscribe();
            }
            if (options?.state) {
                obs.pipe(tap((form) => {
                    setFormState(form, options.state);
                    if (options.state === 'add' && options.revertToken) {
                        form.records.main.revertToken = options.revertToken;
                    }
                }), share()).subscribe();
            }
            return obs.pipe(map((form) => !!form), take(1));
        }
        // TODO: maybe emit an observable with an error
        return of(false);
    }
}
export function convertLegacyQ360Url(url) {
    if (url.startsWith('controller.php?action=')) {
        // handle home page url legacy data from old client 
        url = url.replace('controller.php?action=', '/form/');
        // first instance of & needs to be a ? since the action is no longer a query parameter but there might be other params
        url = url.replace('&', '?');
    }
    else if (url.indexOf('/controller.php?action=') > 0) {
        // strip the domain and subdomain
        url = url.substring(url.indexOf('/controller.php?action='));
        url = url.replace('/controller.php?action=', '/form/');
        // first instance of & needs to be a ? since the action is no longer a query parameter but there might be other params
        url = url.replace('&', '?');
    }
    return url;
}
